import DiscoverPosts from '@/components/DiscoverPosts';
import FollowedPosts from '@/components/FollowedPosts';
import NotificationsRequest from '@/components/NotificationsRequest';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { useUserProfile } from '@/context/UserProfileContext';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/utils/api';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonButton, IonButtons, IonContent, IonHeader, IonLabel, IonModal, IonPage, IonRefresher, IonRefresherContent, IonRouterLink, IonSegment, IonSegmentButton, IonSegmentContent, IonSegmentView, IonToolbar, IonFab, IonFabButton, IonIcon, RefresherEventDetail } from '@ionic/react';
import { add } from 'ionicons/icons';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { postEventService } from '@/services/PostEventService';

const Discover: React.FC = () => {
  document.title = "Discover";

  // State
  const [showNotificationsRequest, setShowNotificationsRequest] = useState(false);
  const [activeTab, setActiveTab] = useState<'discover' | 'followed' | 'nearby'>('discover');
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);

  const { toast } = useToast();

  const { profile, membership } = useUserProfile();
  const isInitialMount = useRef(true);

  // Add a new post to feed
  const addPostToFeed = useCallback((newPost: any) => {
    console.log('New post added:', newPost);
    // The post will be passed to the FollowedPosts component
  }, []);

  // Get user location
  const getGeolocation = useCallback(async () => {
    try {
      setLocationLoading(true);
      // Get location based on platform
      if (Capacitor.isNativePlatform()) {
        // For native platforms, use Capacitor's permission system
        const permissionStatus = await Geolocation.checkPermissions();

        // Request permissions if not granted (handles both 'denied' and 'prompt')
        if (permissionStatus.location !== 'granted') {
          const permissionRequest = await Geolocation.requestPermissions();
          if (permissionRequest.location !== 'granted') {
            toast({
              description: t('location_permission_denied'),
              variant: 'destructive'
            });
            return;
          }
        }

        const { coords } = await Geolocation.getCurrentPosition();
        setLocation({ latitude: coords.latitude, longitude: coords.longitude });
      } else {
        // For web, skip the Capacitor permission request and use browser API directly
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            setLocation({ latitude: coords.latitude, longitude: coords.longitude });
          },
          (err) => {
            console.warn('Web Geolocation error:', err);
            toast({
              description: t('location_error'),
              variant: 'destructive'
            });
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }
    } catch (err) {
      console.warn('Geolocation error:', err);
      toast({
        description: t('location_error'),
        variant: 'destructive'
      });
    } finally {
      setLocationLoading(false);
    }
  }, [toast, t]);
  // Handle refresh for pull-to-refresh functionality
  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    // The refresh will be handled by the individual components
    setTimeout(() => {
      event.detail.complete();
    }, 500);
  };

  // Handle post deletion
  const handleDeletePost = useCallback(
    async (postId: number) => {
      try {
        const response = await api.delete(`/posts/${postId}`);

        if (!response.data) {
          throw new Error('Failed to delete post');
        }

        toast({
          description: (
            <span>
              <FontAwesomeIcon color="green" className="mr-2" icon={faCircleCheck} />
              <span>{t('post_deleted')}</span>
            </span>
          ),
        });

        return response.data;
      } catch (error) {
        console.error('Error deleting post:', error);
        return null;
      }
    },
    [toast, t],
  );

  // Effect for getting location when nearby tab is selected
  useEffect(() => {
    if (activeTab === 'nearby' && !location && !locationLoading) {
      getGeolocation();
    }
  }, [activeTab, location, locationLoading, getGeolocation]);

  // Check notification permissions when component mounts
  useEffect(() => {
    const checkNotificationPermissions = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("FirebaseMessaging")) {
        try {
          const permissionStatus = await FirebaseMessaging.checkPermissions();

          // Show notification request if status is 'prompt' or 'prompt-with-rationale'
          if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
            setShowNotificationsRequest(true);
          }
        } catch (error) {
          console.error('Error checking notification permissions:', error);
        }
      }
    };

    checkNotificationPermissions();
  }, []);

  useEffect(() => {
    if (isInitialMount.current && profile) {
      isInitialMount.current = false;
      // Components will handle their own data loading
    }
  }, [profile]);

  return (
    <IonPage ref={pageRef}>
      <IonContent>
        <div className="mx-auto max-w-lg space-y-4 px-1">
          <IonHeader className="ion-no-border">
            <IonToolbar className="md:hidden">
              <IonButtons slot="start">
                <IonRouterLink routerDirection='forward' routerLink={`/menu`}>
                  <GolfAvatar imageUrl={profile?.imageUrl} name={profile?.name} size={48} className="border-2 border-primary" />
                </IonRouterLink>
                <div className="text-foreground ml-2 flex flex-col">
                  <span className="text-sm">{t('hello')}</span>
                  <span className="text-xl">{profile?.username}</span>
                  {membership?.name && <span className="text-xs text-gray-500">{membership.name}</span>}
                </div>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton routerDirection='forward' routerLink='/messages'>
                  <img src='/assets/images/icons/message.svg' className='size-7 dark:invert' />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          {/* Tabs */}
          <IonSegment value={activeTab} onIonChange={e => setActiveTab(e.detail.value as 'discover' | 'followed' | 'nearby')}>
            <IonSegmentButton contentId="followed" value="followed">
              <IonLabel>{t('followed')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton contentId="discover" value="discover">
              <IonLabel>{t('discover')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>

          {/* Swipeable Content */}
          <IonSegmentView>
            {/* Followed Tab */}
            <IonSegmentContent id="followed">
              {profile && (
                <FollowedPosts
                  profile={profile}
                  handleDeletePost={handleDeletePost}
                  pageRef={pageRef}
                  addPostToFeed={addPostToFeed}
                />
              )}
            </IonSegmentContent>

            {/* Discover Tab */}
            <IonSegmentContent id="discover" className='px-1'>
              {profile && (
                <DiscoverPosts
                  profile={profile}
                  handleDeletePost={handleDeletePost}
                />
              )}
            </IonSegmentContent>
          </IonSegmentView>
        </div>

        {/* Floating action button for mobile */}
        <IonFab vertical="bottom" horizontal="end" className="md:hidden" slot="fixed">
          <IonFabButton onClick={() => postEventService.emit('openCreatePostModal', {})}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {/* Notifications Permission Request Modal */}
        <IonModal isOpen={showNotificationsRequest}>
          <NotificationsRequest
            onConfirm={() => setShowNotificationsRequest(false)}
            onCancel={() => setShowNotificationsRequest(false)}
          />
        </IonModal>
      </IonContent >
    </IonPage >
  );
};

export default Discover;
